// File: src/components/artistDashboard/chartingServices/PromoteSingleModal.js

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

// Assets: 3 levels up to src/assets
import tipIcon from '../../../assets/tip_icon.png';
import tipWhite from '../../../assets/tip_white.png';
import IndieHustlerImg from '../../../assets/Indiehustler.png';
import ActivateFansImg from '../../../assets/ActivateFans.png';
import StartTheBuzzImg from '../../../assets/StartTheBuzz.png';
import GetFeaturedImg from '../../../assets/GetFeatured.png';
import VIPAccessImg from '../../../assets/VIPAccess.png';

// Sub-services and builder modals
import CampaignBuilderModal from '../labelServices/CampaignBuilderModal';
import StartTheBuzzModal from './StartTheBuzzModal';

const PromoteSingleModal = ({ isOpen, onClose, song, artistName, lifetimePoints = 0, isDarkMode }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [isBuzzModalOpen, setIsBuzzModalOpen] = useState(false);

  // VIP Fast-Track Form State
  const [vipForm, setVipForm] = useState({
    epkLink: '',
    announcementText: '',
    socialLinks: '',
    supportingDocs: '',
  });
  const [isSubmittingVip, setIsSubmittingVip] = useState(false);

  if (!isOpen || !song) return null;

  const isTopTwenty = (song.position || 99) <= 20;
  const chartName = isTopTwenty ? 'Top Twenty' : 'Nuclear Fifty';

  const shareMessage = `Hey! My song '${song.title}' is currently charting in the Atomic ${chartName} charts! To help us climb the ranks I need 10 core legends to back the track on Atomic. You can earn free app points by rating tracks on the Trends page - once you hit 50 points, tip my song to unlock the download (which gives us a massive chart boost!). Jump on the Atomic app and let's get this happening!`;

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setIsCopied(true);
      toast.success("Message copied to clipboard!");
      setTimeout(() => setIsCopied(false), 2500);
    } catch (err) {
      toast.error("Failed to copy to clipboard.");
    }
  };

  const handleOptionClick = (optionKey) => {
    if (optionKey === 'start_the_buzz') {
      setIsBuzzModalOpen(true);
      return;
    }
    if (optionKey === 'activate_fans') {
      setIsCampaignModalOpen(true);
      return;
    }
    if (optionKey === 'vip_access' && lifetimePoints < 50000) {
      setSelectedOption('VIP_FAST_TRACK');
      return;
    }
    setSelectedOption(optionKey);
  };

  const handleVipSubmit = (e) => {
    e.preventDefault();
    setIsSubmittingVip(true);
    setTimeout(() => {
      setIsSubmittingVip(false);
      toast.success("VIP Fast-Track application submitted for review!");
      setSelectedOption(null);
    }, 800);
  };

  const activeTipIcon = isDarkMode ? tipWhite : tipIcon;

  const options = [
    {
      key: 'indie_hustler',
      title: 'Indie Hustler',
      image: IndieHustlerImg,
      badgeLabel: 'FREE',
      badgeIcon: null,
      description: 'Mobilise your friends, fans and fam to tip and download your single to boost rank.',
    },
    {
      key: 'activate_fans',
      title: 'Activate Fans',
      image: ActivateFansImg,
      badgeLabel: 'FREE',
      badgeIcon: null,
      description: 'Build a custom campaign post to promote your track on Atomic and beyond.',
    },
    {
      key: 'start_the_buzz',
      title: 'Start The Buzz',
      image: StartTheBuzzImg,
      badgeLabel: 'PAID',
      badgeIcon: activeTipIcon,
      costDollars: 75,
      pointsRequired: 75000,
      description: 'Ignite your release with sponsored network placements.',
    },
    {
      key: 'get_featured',
      title: 'Get Featured',
      image: GetFeaturedImg,
      badgeLabel: 'PAID',
      badgeIcon: activeTipIcon,
      costDollars: 225,
      pointsRequired: 225000,
      description: 'Major network coverage plus campaign broader industry placement.',
    },
    {
      key: 'vip_access',
      title: 'VIP Access',
      image: VIPAccessImg,
      badgeLabel: lifetimePoints < 50000 ? 'LOCKED' : 'PAID',
      badgeIcon: activeTipIcon,
      costDollars: 480,
      pointsRequired: 480000,
      description: 'Requires 50,000 lifetime points or an approved Fast-Track Application. Maximum network distribution across Press, Playlists, Blog, Radio, Trends and more.',
    },
  ];

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border p-5 shadow-2xl transition-all ${
              isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-gray-200 text-slate-900'
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-zinc-800/60 mb-4">
              <div>
                <h3 className="text-sm font-black uppercase tracking-wider text-brand-pink">
                  Promote Higher
                </h3>
                <p className="text-xs text-gray-400 font-medium mt-0.5">
                  Choose a promotion tier for "{song.title}"
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-lg font-bold p-1 leading-none"
              >
                &times;
              </button>
            </div>

            {/* Options List */}
            <div className="space-y-3.5">
              {options.map((opt) => (
                <div
                  key={opt.key}
                  onClick={() => handleOptionClick(opt.key)}
                  className={`p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col gap-2.5 ${
                    isDarkMode
                      ? 'bg-zinc-900 border-zinc-800 hover:border-blue-500/50 hover:bg-zinc-850'
                      : 'bg-gray-50 border-gray-200 hover:border-blue-500/50 hover:bg-gray-100'
                  }`}
                >
                  {/* Top Row: Full Title + Uniform Blue Badge */}
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-xs sm:text-sm font-black uppercase tracking-wider text-slate-900 dark:text-white whitespace-normal">
                      {opt.title}
                    </h4>

                    <div className="flex-shrink-0 h-6 min-w-[68px] px-2.5 rounded-full border bg-blue-500/10 text-blue-400 border-blue-500/25 flex items-center justify-center gap-1">
                      {opt.badgeIcon && (
                        <img src={opt.badgeIcon} alt="Tip" className="w-3 h-3 object-contain" />
                      )}
                      <span className="text-[10px] font-black uppercase tracking-wider leading-none">
                        {opt.badgeLabel}
                      </span>
                    </div>
                  </div>

                  {/* Bottom Row: 4:3 Image + Description */}
                  <div className="flex items-center gap-3.5">
                    <div className="relative w-28 sm:w-32 aspect-[4/3] flex-shrink-0 rounded-lg overflow-hidden border border-zinc-700/50 bg-black shadow-sm">
                      <img 
                        src={opt.image} 
                        alt={opt.title} 
                        className="w-full h-full object-cover" 
                      />
                    </div>

                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <p className="text-[11px] sm:text-xs text-gray-400 leading-relaxed">
                        {opt.description}
                      </p>

                      {opt.costDollars && (
                        <p className="text-[10px] text-gray-500 mt-1.5 font-medium">
                          Cost: ${opt.costDollars} or {opt.pointsRequired.toLocaleString()} fan points (10 pts = $0.01)
                        </p>
                      )}

                      {opt.key === 'vip_access' && lifetimePoints < 50000 && (
                        <p className="text-[10px] font-bold text-blue-400 mt-1">
                          Tap to submit VIP Fast-Track Application ({lifetimePoints.toLocaleString()}/50,000 pts)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Sub-modal: Indie Hustler Share Message */}
      <AnimatePresence>
        {selectedOption === 'indie_hustler' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={() => setSelectedOption(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-md rounded-2xl border p-5 shadow-2xl ${
                isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-gray-200 text-slate-900'
              }`}
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white">
                  Indie Hustler: Shareable Message
                </h3>
                <button
                  onClick={() => setSelectedOption(null)}
                  className="text-gray-400 hover:text-white font-bold"
                >
                  &times;
                </button>
              </div>

              <p className="text-xs text-gray-300 leading-relaxed bg-zinc-900/80 border border-zinc-800 p-3 rounded-xl mb-3 select-all">
                "{shareMessage}"
              </p>

              <p className="text-[11px] text-gray-400 italic mb-4">
                Share this message with as many of your friends, fans and family as you can!
              </p>

              <button
                onClick={handleCopyMessage}
                className="w-full bg-brand-pink text-white font-black text-xs py-2.5 rounded-xl shadow-lg shadow-brand-pink/25 hover:opacity-95 active:scale-98 transition-all flex items-center justify-center gap-1.5"
              >
                {isCopied ? "Copied to Clipboard! ✓" : "Copy to Clipboard"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sub-modal: Get Featured & VIP Access Details */}
      <AnimatePresence>
        {(selectedOption === 'get_featured' || selectedOption === 'vip_access') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={() => setSelectedOption(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-md rounded-2xl border p-5 shadow-2xl ${
                isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-gray-200 text-slate-900'
              }`}
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white">
                  {selectedOption === 'get_featured' && 'Get Featured'}
                  {selectedOption === 'vip_access' && 'VIP Access'}
                </h3>
                <button
                  onClick={() => setSelectedOption(null)}
                  className="text-gray-400 hover:text-white font-bold"
                >
                  &times;
                </button>
              </div>

              <div className="space-y-3 text-xs text-gray-300">
                {selectedOption === 'get_featured' && (
                  <div className="space-y-2">
                    <p>Includes:</p>
                    <ul className="list-disc pl-4 space-y-1 text-gray-400">
                      <li>Sponsored placement in Trends Feed</li>
                      <li>Add-To-Playlist Modal feature</li>
                      <li>Radio Station ID recording: <em>"Hi this is {artistName || 'the artist'} and you're listening to Atomic"</em></li>
                      <li>Radio Song Intro recording: <em>"Hey this is {artistName || 'the artist'} and you're about to listen to my new single '{song.title}' on Atomic"</em></li>
                    </ul>
                  </div>
                )}

                {selectedOption === 'vip_access' && (
                  <div className="space-y-2">
                    <p>Includes full network rollout:</p>
                    <ul className="list-disc pl-4 space-y-1 text-gray-400">
                      <li>Song Embedded Announcement & EPK video/audio assets</li>
                      <li>Sponsored Press Feed distribution to Hot Raters, Playlisters & Subscribers</li>
                      <li>Homepage Blog Feature</li>
                      <li>Trends Feed & Add-To-Playlist placement</li>
                      <li>Station ID & Single Intro broadcasts</li>
                    </ul>
                  </div>
                )}

                <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-800 mt-2">
                  <p className="font-bold text-white">Payment Options:</p>
                  <p className="text-gray-400 mt-1">
                    Direct Payment: <strong className="text-white">${selectedOption === 'get_featured' ? 149 : 319} AUD</strong>
                  </p>
                  <p className="text-gray-400">
                    Fan Points: <strong className="text-brand-pink">{((selectedOption === 'get_featured' ? 149 : 319) * 1000).toLocaleString()} pts</strong> (10 pts = $0.01)
                  </p>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => {
                    toast.success("Order initiated. Redirecting to payment...");
                    setSelectedOption(null);
                  }}
                  className="flex-1 bg-brand-pink text-white font-black text-xs py-2.5 rounded-xl shadow-lg shadow-brand-pink/25 hover:opacity-95 transition-all"
                >
                  Continue
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sub-modal: VIP Fast-Track Application Form */}
      <AnimatePresence>
        {selectedOption === 'VIP_FAST_TRACK' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={() => setSelectedOption(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border p-5 shadow-2xl ${
                isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-gray-200 text-slate-900'
              }`}
            >
              <div className="flex justify-between items-center pb-2 mb-3 border-b border-zinc-800/60">
                <div>
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white">
                    VIP Fast-Track Application
                  </h3>
                  <p className="text-[11px] text-gray-400">
                    Apply for early VIP placement for "{song.title}"
                  </p>
                </div>
                <button
                  onClick={() => setSelectedOption(null)}
                  className="text-gray-400 hover:text-white font-bold"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleVipSubmit} className="space-y-3 text-xs">
                <div>
                  <label className="block text-[11px] font-bold text-gray-300 mb-1">
                    Audio / Video EPK Assets (Link or Drive URL) *
                  </label>
                  <input
                    type="url"
                    required
                    placeholder="https://drive.google.com/..."
                    value={vipForm.epkLink}
                    onChange={(e) => setVipForm({ ...vipForm, epkLink: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all ${
                      isDarkMode 
                        ? 'bg-zinc-900 border-zinc-800 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-slate-900 focus:border-blue-500'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-gray-300 mb-1">
                    Song Embedded Announcement Message *
                  </label>
                  <textarea
                    rows={2}
                    required
                    placeholder="Provide the announcement or artist message to feature with this track..."
                    value={vipForm.announcementText}
                    onChange={(e) => setVipForm({ ...vipForm, announcementText: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all ${
                      isDarkMode 
                        ? 'bg-zinc-900 border-zinc-800 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-slate-900 focus:border-blue-500'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-gray-300 mb-1">
                    Social Media Handles & Links *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Instagram, TikTok, Spotify links..."
                    value={vipForm.socialLinks}
                    onChange={(e) => setVipForm({ ...vipForm, socialLinks: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all ${
                      isDarkMode 
                        ? 'bg-zinc-900 border-zinc-800 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-slate-900 focus:border-blue-500'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-gray-300 mb-1">
                    Supporting Text, Press Links & Documents (Optional)
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Paste press releases, reviews, or cloud document links..."
                    value={vipForm.supportingDocs}
                    onChange={(e) => setVipForm({ ...vipForm, supportingDocs: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-xs font-medium focus:outline-none transition-all ${
                      isDarkMode 
                        ? 'bg-zinc-900 border-zinc-800 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-slate-900 focus:border-blue-500'
                    }`}
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmittingVip}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black text-xs py-2.5 rounded-xl shadow-lg shadow-blue-600/25 active:scale-98 transition-all"
                  >
                    {isSubmittingVip ? "Submitting Application..." : "Submit VIP Fast-Track Application"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Start The Buzz Preview & Checkout Modal */}
      <StartTheBuzzModal
        isOpen={isBuzzModalOpen}
        onClose={() => setIsBuzzModalOpen(false)}
        song={song}
        isDarkMode={isDarkMode}
      />

      {/* CampaignBuilderModal triggered by Activate Fans */}
      {isCampaignModalOpen && (
        <CampaignBuilderModal
          isOpen={isCampaignModalOpen}
          onClose={() => setIsCampaignModalOpen(false)}
          initialSong={song}
          onCampaignCreated={(postUrl) => {
            setIsCampaignModalOpen(false);
            if (postUrl) {
              navigator.clipboard.writeText(postUrl);
              toast.success("Post URL copied to clipboard!");
            }
          }}
        />
      )}
    </>
  );
};

export default PromoteSingleModal;